import { fetchDashboardDeferred, fetchPartnerDashboardJobStatus, fetchPlatformDashboardJobStatus } from "../api/client";

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isDeferredDashboardMeta(payload) {
  return payload && typeof payload === "object" && payload.status === "computing" && payload.jobId;
}

function looksLikeDashboardPayload(body) {
  if (!body || typeof body !== "object" || Array.isArray(body)) return false;
  return (
    body.executive != null ||
    body.cards != null ||
    body.charts != null ||
    Array.isArray(body.questions) ||
    body.operadores != null
  );
}

export function parseDashboardJobPollBody(jobRes) {
  const jobBody = jobRes?.data ?? jobRes;
  if (!jobBody || typeof jobBody !== "object") return { kind: "pending" };

  if (jobBody.status === "ready") {
    if (jobBody.data) return { kind: "ready", payload: jobBody.data };
    if (looksLikeDashboardPayload(jobBody)) return { kind: "ready", payload: jobBody };
  }

  if (jobBody.status === "failed") {
    return { kind: "failed", error: jobBody.error };
  }

  if (jobBody.status === "cancelled" || jobBody.status === "cancel_requested") {
    return { kind: "cancelled" };
  }

  if (!jobBody.status && looksLikeDashboardPayload(jobBody)) {
    return { kind: "ready", payload: jobBody };
  }

  return { kind: "pending", status: jobBody.status || null };
}

export async function pollDashboardJob(jobId, pollAfterMs = 3000, maxWaitMs = 600_000, options = {}) {
  const signal = options?.signal;
  const started = Date.now();
  let delay = Math.max(1500, Number(pollAfterMs) || 3000);

  while (Date.now() - started < maxWaitMs) {
    if (signal?.aborted) {
      const err = new Error("dashboard_job_poll_aborted");
      err.cancelled = true;
      throw err;
    }
    await sleep(delay);
    if (signal?.aborted) {
      const err = new Error("dashboard_job_poll_aborted");
      err.cancelled = true;
      throw err;
    }
    const jobRes = await fetchDashboardDeferred(`/dashboard/jobs/${encodeURIComponent(jobId)}`);
    const parsed = parseDashboardJobPollBody(jobRes);
    if (parsed.kind === "ready") {
      return parsed.payload;
    }
    if (parsed.kind === "failed") {
      const err = new Error("dashboard_job_failed");
      err.userFacing = "Não foi possível concluir o relatório. Tente novamente em instantes.";
      throw err;
    }
    if (parsed.kind === "cancelled") {
      const err = new Error("dashboard_job_cancelled");
      err.userFacing = "A geração deste relatório foi cancelada.";
      err.cancelled = true;
      throw err;
    }
    delay = Math.min(Math.floor(delay * 1.15), 8000);
  }

  const err = new Error("dashboard_job_timeout");
  err.userFacing = "O relatório ainda está sendo processado. Você será avisado quando estiver pronto.";
  throw err;
}

export async function pollPartnerDashboardJob(jobId, pollAfterMs = 3000, maxWaitMs = 600_000, options = {}) {
  const signal = options?.signal;
  const started = Date.now();
  let delay = Math.max(1500, Number(pollAfterMs) || 3000);

  while (Date.now() - started < maxWaitMs) {
    if (signal?.aborted) {
      const err = new Error("partner_dashboard_job_poll_aborted");
      err.cancelled = true;
      throw err;
    }
    await sleep(delay);
    if (signal?.aborted) {
      const err = new Error("partner_dashboard_job_poll_aborted");
      err.cancelled = true;
      throw err;
    }
    const jobRes = await fetchPartnerDashboardJobStatus(jobId);
    const parsed = parseDashboardJobPollBody(jobRes);
    if (parsed.kind === "ready") {
      return parsed.payload;
    }
    if (parsed.kind === "failed") {
      const err = new Error("partner_dashboard_job_failed");
      err.userFacing = "Não foi possível concluir o relatório consolidado. Tente novamente em instantes.";
      throw err;
    }
    if (parsed.kind === "cancelled") {
      const err = new Error("partner_dashboard_job_cancelled");
      err.userFacing = "A geração deste relatório foi cancelada.";
      err.cancelled = true;
      throw err;
    }
    delay = Math.min(Math.floor(delay * 1.15), 8000);
  }

  const err = new Error("partner_dashboard_job_timeout");
  err.userFacing =
    "O relatório consolidado ainda está sendo processado. Você será avisado quando estiver pronto.";
  throw err;
}

export async function pollPlatformDashboardJob(jobId, pollAfterMs = 3000, maxWaitMs = 600_000, options = {}) {
  const signal = options?.signal;
  const started = Date.now();
  let delay = Math.max(1500, Number(pollAfterMs) || 3000);

  while (Date.now() - started < maxWaitMs) {
    if (signal?.aborted) {
      const err = new Error("platform_dashboard_job_poll_aborted");
      err.cancelled = true;
      throw err;
    }
    await sleep(delay);
    if (signal?.aborted) {
      const err = new Error("platform_dashboard_job_poll_aborted");
      err.cancelled = true;
      throw err;
    }
    const jobRes = await fetchPlatformDashboardJobStatus(jobId);
    const parsed = parseDashboardJobPollBody(jobRes);
    if (parsed.kind === "ready") {
      return parsed.payload;
    }
    if (parsed.kind === "failed") {
      const err = new Error("platform_dashboard_job_failed");
      err.userFacing = "Não foi possível concluir o relatório consolidado. Tente novamente em instantes.";
      throw err;
    }
    if (parsed.kind === "cancelled") {
      const err = new Error("platform_dashboard_job_cancelled");
      err.userFacing = "A geração deste relatório foi cancelada.";
      err.cancelled = true;
      throw err;
    }
    delay = Math.min(Math.floor(delay * 1.15), 8000);
  }

  const err = new Error("platform_dashboard_job_timeout");
  err.userFacing =
    "O relatório consolidado ainda está sendo processado. Você será avisado quando estiver pronto.";
  throw err;
}
